import { Box } from '@mui/material';
import clsx from 'clsx';
import { ReactNode } from 'react';

import { Typography, TypographyProps } from '../../Typography';

import {
  InfoTooltip,
  InfoTooltipProps,
} from '../../../components/tooltip/InfoTooltip';
import { tokens } from '../../../theme/generated/tokens';
import { WithRef } from '../../../types';
import { mergeSx } from '../../../utils';

import { labelClassNames } from './classNames';
import { labelSizeToVariant, LabelSize, LabelWeight } from './types';

export type LabelSlotProps = {
  infoTooltip?: Partial<InfoTooltipProps>;
};

/**
 * Atomic label row: text + optional required asterisk + optional info tooltip.
 * With `htmlFor` the row is a native `<label>` (click activates the control);
 * without it, a `<span>` (group-heading case). The tooltip is a sibling of the
 * label element, so clicking it never activates the control.
 */
export type LabelProps = Omit<
  TypographyProps,
  'children' | 'fontSize' | 'color'
> & {
  /** Label text. */
  label: ReactNode;
  /**
   * The id of the control this label points AT. Present → native `<label>`,
   * absent → `<span>` (group heading).
   */
  htmlFor?: string;
  /** Label size variant. Defaults to sm. */
  size?: LabelSize;
  /**
   * Label font weight. Precedence: explicit labelWeight -> variant ->
   * medium default.
   */
  labelWeight?: LabelWeight;
  /** Marks the label required and renders a red asterisk next to the text. */
  required?: boolean;
  /** Content for the info tooltip rendered after the label text. */
  infoTooltip?: ReactNode;
  /** Disabled state: dims the label text and asterisk. */
  disabled?: boolean;
  /** Per-slot props for the info tooltip. */
  slotProps?: LabelSlotProps;
  /** `data-testid` attribute value */
  testId?: string;
};

export const Label = ({
  ref,
  label,
  htmlFor,
  size = 'sm',
  labelWeight,
  required,
  infoTooltip,
  disabled,
  slotProps,
  variant,
  className,
  sx,
  testId,
  ...typographyProps
}: WithRef<HTMLDivElement, LabelProps>) => {
  const resolvedVariant =
    labelWeight !== undefined
      ? labelSizeToVariant[size][labelWeight]
      : variant ?? labelSizeToVariant[size].medium;

  return (
    <Box
      ref={ref}
      data-testid={testId}
      className={clsx(
        labelClassNames.root,
        disabled && labelClassNames.disabled,
        className
      )}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {/* A real <label> only when pointing at a control; group headings use a
          <span> (a <label> names exactly one control). */}
      <Typography
        {...typographyProps}
        component={htmlFor ? 'label' : 'span'}
        {...(htmlFor ? { htmlFor } : {})}
        className={labelClassNames.label}
        variant={resolvedVariant}
        sx={mergeSx(
          {
            display: 'inline-flex',
            // Only a label pointing at a control is a click target.
            cursor: htmlFor && !disabled ? 'pointer' : 'default',
            color: disabled
              ? tokens.theme.disabled.fg
              : tokens.theme.fg.structural.primary,
          },
          sx
        )}
      >
        {label}
        {/* Inline so it follows the last word on wrap. */}
        {required && (
          <Box
            component="span"
            aria-hidden
            className={labelClassNames.asterisk}
            sx={{
              marginLeft: '2px',
              color: disabled
                ? tokens.theme.disabled.fg
                : tokens.theme.fg.status.error,
            }}
          >
            *
          </Box>
        )}
      </Typography>
      {/* Sibling of the label element — outside the click target, so clicking
          the icon never toggles/focuses the control. */}
      {!!infoTooltip && (
        <Box
          className={labelClassNames.tooltip}
          sx={{ display: 'inline-flex', marginLeft: '2px' }}
        >
          <InfoTooltip
            iconSize={16}
            color={tokens.theme.fg.structural.primary}
            {...slotProps?.infoTooltip}
            title={infoTooltip}
          />
        </Box>
      )}
    </Box>
  );
};

Label.displayName = 'Label';
